import { createFileRoute, useNavigate, Outlet, useMatches } from '@tanstack/react-router'
import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useScrollRestoration } from '@/shared/lib/use-scroll-restoration'
import { z } from 'zod'
import { AppShell } from '@/shared/ui/app-shell'
import { BottomNav } from '@/widgets/bottom-nav'
import { MainHeader, PageTitle, Skeleton, ConfirmDialog } from '@/shared/ui'
import { useTelegram, useMainButton } from '@/shared/lib/telegram'
import { requireAuth } from '@/shared/lib/router'
import { useInfiniteDrinkHistory, useDeleteDrinkHistoryEntry } from '@/features/drink-history/api/use-drink-history'
import {
  SearchFiltersBar,
  DiaryEntryCard,
  MonthSeparator,
  EmptyState,
  NoResultsState,
} from '@/widgets/tea-diary'
import { Loader2 } from 'lucide-react'

// Цвета MainButton для разных тем
const MAIN_BUTTON_COLORS = {
  light: { color: '#C73B0D', textColor: '#FFFFFF' },
  dark: { color: '#E84D1C', textColor: '#FFFFFF' },
}

const teaDiarySearchSchema = z.object({
  filter: z.string().optional(),
  search: z.string().optional(),
})

export const Route = createFileRoute('/tea-diary')({
  validateSearch: teaDiarySearchSchema,
  component: TeaDiaryLayout,
})

/**
 * Layout для чайного дневника
 */
function TeaDiaryLayout() {
  const matches = useMatches()
  const hasChildRoute = matches.some(
    (match) => match.routeId === '/tea-diary/add' || match.routeId === '/tea-diary/edit/$entryId'
  )
  
  if (hasChildRoute) {
    return <Outlet />
  }
  
  return <TeaDiaryPage />
}

/**
 * Страница чайного дневника
 * 
 * Унифицированный дизайн с Главной и Каталогом:
 * - MainHeader (лого + аватар)
 * - PageTitle (заголовок + описание)
 * - MainButton для добавления записи
 */
function TeaDiaryPage() {
  const { initData, hapticFeedback, webApp } = useTelegram()
  const { search: urlSearch } = Route.useSearch()
  const navigate = useNavigate()
  
  // Scroll restoration при навигации назад
  const containerRef = useScrollRestoration('tea-diary')

  const [searchQuery, setSearchQuery] = useState(urlSearch || '')
  const [filters, setFilters] = useState<{ teaType?: string; rating?: number }>({})
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null)

  // Определяем тему для цветов кнопки
  const theme = webApp?.colorScheme || 'light'
  const buttonColors = MAIN_BUTTON_COLORS[theme]

  // Нативная MainButton "Добавить запись"
  const handleAddClick = useCallback(() => {
    hapticFeedback.impactOccurred('light')
    navigate({ to: '/tea-diary/add' })
  }, [navigate, hapticFeedback])
  
  useMainButton({
    text: 'Записать чай',
    onClick: handleAddClick,
    color: buttonColors.color,
    textColor: buttonColors.textColor,
  })

  requireAuth(initData)

  const { 
    data, 
    isLoading, 
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteDrinkHistory({}, 20)
  
  const deleteEntry = useDeleteDrinkHistoryEntry()

  // Объединяем все страницы в один массив
  const entries = useMemo(() => {
    if (!data?.pages) return []
    return data.pages.flatMap(page => page.data)
  }, [data?.pages])

  // Intersection Observer для автоматической подгрузки
  const loadMoreRef = useRef<HTMLDivElement>(null)
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    )

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Фильтрация
  const filteredEntries = useMemo(() => {
    let result = entries
    
    // Поиск по названию чая и заметкам
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim()
      result = result.filter((e) => 
        e.teaName?.toLowerCase().includes(query) ||
        e.notes?.toLowerCase().includes(query) ||
        e.tea?.name?.toLowerCase().includes(query)
      )
    }
    
    // Фильтр по типу чая (категории)
    if (filters.teaType) {
      const filterType = filters.teaType.toLowerCase()
      
      result = result.filter((e) => {
        const categoryName = e.tea?.categoryName?.toLowerCase() || ''
        
        if (categoryName === filterType) {
          return true
        }
        
        if (categoryName && (categoryName.includes(filterType) || filterType.includes(categoryName))) {
          return true
        }
        
        const categorySlug = e.tea?.categorySlug?.toLowerCase() || ''
        if (categorySlug && (categorySlug.includes(filterType) || filterType.includes(categorySlug))) {
          return true
        }
        
        return false
      })
    }
    
    // Фильтр по рейтингу
    if (filters.rating) {
      result = result.filter((e) => e.rating && e.rating >= filters.rating!)
    }
    
    return result
  }, [entries, filters, searchQuery])

  // Группировка по месяцам
  const groupedEntries = useMemo(() => {
    const groups: Record<string, typeof filteredEntries> = {}
    
    filteredEntries.forEach((entry) => {
      const date = new Date(entry.drankAt)
      const key = `${date.getFullYear()}-${date.getMonth()}`
      if (!groups[key]) groups[key] = []
      groups[key].push(entry)
    })

    const monthNames = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ]

    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, items]) => {
        const [year, month] = key.split('-').map(Number)
        return {
          key,
          month: monthNames[month],
          year,
          entries: items.sort((a, b) => 
            new Date(b.drankAt).getTime() - new Date(a.drankAt).getTime()
          ),
        }
      })
  }, [filteredEntries])

  const hasResults = filteredEntries.length > 0
  const hasFilters = searchQuery || Object.keys(filters).length > 0
  const isEmptyState = !isLoading && !error && !hasResults && !hasFilters

  const handleEdit = useCallback((entryId: string) => {
    hapticFeedback.impactOccurred('light')
    navigate({ to: '/tea-diary/edit/$entryId', params: { entryId } })
  }, [navigate, hapticFeedback])

  const handleDelete = useCallback((entryId: string) => {
    hapticFeedback.impactOccurred('medium')
    setEntryToDelete(entryId)
    setDeleteDialogOpen(true)
  }, [hapticFeedback])

  const confirmDelete = useCallback(async () => {
    if (!entryToDelete) return
    
    try {
      await deleteEntry.mutateAsync(entryToDelete)
      hapticFeedback.notificationOccurred('success')
    } catch {
      hapticFeedback.notificationOccurred('error')
    } finally {
      setDeleteDialogOpen(false)
      setEntryToDelete(null)
    }
  }, [entryToDelete, deleteEntry, hapticFeedback])

  const handleDuplicate = useCallback((entryId: string) => {
    const entry = entries.find((e) => e.id === entryId)
    if (entry) {
      hapticFeedback.impactOccurred('light')
      navigate({
        to: '/tea-diary/add',
        search: { 
          teaId: entry.teaId || undefined,
        },
      })
    }
  }, [entries, navigate, hapticFeedback])

  // Подзаголовок с количеством записей
  const totalCount = data?.pages?.[0]?.total || entries.length
  const subtitle = useMemo(() => {
    if (isLoading) return 'Загрузка...'
    if (totalCount === 0) return 'Начните записывать впечатления'
    const word = totalCount === 1 ? 'запись' : totalCount < 5 ? 'записи' : 'записей'
    return `${totalCount} ${word} о чае`
  }, [totalCount, isLoading])

  return (
    <AppShell showBottomNav>
      <div
        ref={containerRef}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-paper dark:bg-[#0F1113] pt-safe"
      >
        {/* Контейнер с адаптивными отступами — как на главной */}
        <div className="min-h-full content-container py-6">
          {/* Унифицированный хедер */}
          <MainHeader />
          
          {/* Заголовок страницы */}
          <PageTitle 
            title="Дневник" 
            subtitle={subtitle}
            className="mt-6"
          />

        {isEmptyState ? (
            <div className="flex-1 flex items-center justify-center mt-12">
            <EmptyState />
          </div>
        ) : (
            <div className="mt-6">
              {/* Поиск и фильтры */}
            <SearchFiltersBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              filters={filters}
              onFiltersChange={setFilters}
            />

              {/* Записи */}
              <div className="mt-4">
              {isLoading ? (
                  <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-28 w-full rounded-2xl" />
                  ))}
                </div>
              ) : error ? (
                  <div className="text-center py-12">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Не удалось загрузить записи
                  </p>
                </div>
              ) : !hasResults ? (
                hasFilters && (
                  <NoResultsState
                    onResetFilters={() => {
                      setSearchQuery('')
                      setFilters({})
                    }}
                  />
                )
              ) : (
                <div className="space-y-2">
                  {groupedEntries.map(({ key, month, year, entries: monthEntries }) => (
                    <div key={key}>
                      <MonthSeparator month={month} year={year} />
                        <div className="space-y-3">
                        {monthEntries.map((entry) => (
                          <DiaryEntryCard
                            key={entry.id}
                            entry={entry}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onDuplicate={handleDuplicate}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                  
                  {/* Элемент для автоматической подгрузки */}
                  <div ref={loadMoreRef} className="py-4">
                    {isFetchingNextPage && (
                      <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="text-sm">Загрузка...</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </div>

      <BottomNav />
      
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setEntryToDelete(null)
        }}
        onConfirm={confirmDelete}
        title="Удалить запись?"
        description="Запись будет удалена безвозвратно"
        confirmText="Удалить"
        cancelText="Отмена"
        variant="danger"
        isLoading={deleteEntry.isPending}
      />
    </AppShell>
  )
}
